<template>
  <div class="map-container">
    <gmap-map
      ref="CoverageMap"
      :center="center"
      :zoom="13"
      :options="mapOptions"
      map-type-id="roadmap"
      class="coverage-map">

      <gmap-polygon v-for="(sector, index) in sectorItems" v-if="showPolygons"
        :key="'P' + sector.ID"
        :paths="sector.polygon"
        :options="{
          strokeColor: sector.overlayColor,
          strokeOpacity: 1,
          strokeWeight: sector.polygonOptions.strokeWeight,
          fillColor: sector.overlayColor,
          fillOpacity: sector.polygonOptions.fillOpacity,
          zIndex: sector.polygonOptions.zIndex
        }"
        @click="centerSector(sector, index)">
      </gmap-polygon>

      <gmap-marker v-for="(sector, index) in sectorItems" v-if="showPolygons"
        :key="'M' + sector.ID"
        :position="getCenter(sector)"
        :icon="map.labelBackground"
        :label="{
          text: sector.name,
          color: '#333333',
          fontSize: '14px',
          fontFamily: 'Open Sans',
          fontWeight: '700'
        }"
        :zIndex="2"
        @click="centerSector(sector, index)">
      </gmap-marker>

      <!-- RESERVATION INFO -->
      <gmap-marker v-if="rsvp !== null && !rsvp.isPackage"
        :key="'RSVP_PU'"
        :icon="map.poiPin"
        :position="{ lat: rsvp.pickupLocation.lat, lng: rsvp.pickupLocation.lng }"
        :zIndex="2"
        @click="infoWindows.pickup = !infoWindows.pickup">
      </gmap-marker>
      <gmap-info-window v-if="rsvp !== null && !rsvp.isPackage && infoWindows.pickup"
        :key="'RSVP_PUINFO'"
        :position="{ lat: rsvp.pickupLocation.lat, lng: rsvp.pickupLocation.lng }"
        :zIndex="3"
        @closeclick="infoWindows.pickup = false">
        <div class="point-info">
          <div class="label-value" v-if="rsvpInfo.pickupTime !== null">
            <label>PICKUP TIME</label>
            <div class="value">{{ rsvpInfo.pickupTime.format('MM/DD/YYYY hh:mmA') }}</div>
          </div>
        </div>
      </gmap-info-window>
      <gmap-marker v-if="rsvp !== null && !rsvp.isPackage"
        :key="'RSVP_DO'"
        :icon="map.poiPin"
        :position="{ lat: rsvp.dropoffLocation.lat, lng: rsvp.dropoffLocation.lng }"
        :zIndex="2"
        @click="infoWindows.dropoff = !infoWindows.dropoff">
      </gmap-marker>
      <gmap-info-window v-if="rsvp !== null && !rsvp.isPackage && infoWindows.dropoff"
        :key="'RSVP_DOINFO'"
        :position="{ lat: rsvp.dropoffLocation.lat, lng: rsvp.dropoffLocation.lng }"
        :zIndex="3"
        @closeclick="infoWindows.dropoff = false">
        <div class="point-info">
          <div class="label-value" v-if="rsvpInfo.dropoffTime !== null">
            <label>ESTIMATED DROP-OFF TIME</label>
            <div class="value">{{ rsvpInfo.dropoffTime.format('MM/DD/YYYY hh:mmA') }}</div>
          </div>
          <div class="label-value" v-if="rsvpInfo.distanceInMiles !== null">
            <label>TRAVEL DISTANCE</label>
            <div class="value">{{ rsvpInfo.distanceInMiles.toFixed(2) }} miles</div>
          </div>
        </div>
      </gmap-info-window>

      <!-- PACKAGE INFO -->
      <div v-if="rsvp !== null && rsvp.isPackage">
        <gmap-marker v-for="(stop, index) in rsvp.stops"
          :key="'PACKAGE_POI' + index"
          :icon="map.poiPin"
          :position="{ lat: stop.lat, lng: stop.lng }"
          :zIndex="2"
          @click="toggleStopsInfo(index)">
        </gmap-marker>

        <gmap-info-window v-for="(stop, index) in rsvp.stops" v-if="stopsInfo[index]"
          :key="'PACKAGE_INFO' + index"
          :position="{ lat: stop.lat, lng: stop.lng }"
          :zIndex="3"
          @closeclick="toggleStopsInfo(index)">
          <div class="point-info">
            <div class="label-value">
              <label>RESERVATION ID</label>
              <div class="value">
                <a :href="'/#/reservation/' + stop.reservationID" target="_blank">{{ stop.reservationID }}</a>
              </div>
            </div>
            <div class="label-value">
              <label v-if="stop.isPickup">ESTIMATED PICK-UP TIME</label>
              <label v-else>ESTIMATED DROP-OFF TIME</label>
              <div class="value">{{ stop.etaTime.format('MM/DD/YYYY hh:mmA') }}</div>
            </div>
          </div>
        </gmap-info-window>
      </div>

      <gmap-polyline v-if="rsvp !== null"
        :path="path.base"
        :options="{strokeColor:'#999999'}"
        :zIndex="1">
      </gmap-polyline>
      <gmap-polyline v-if="rsvp !== null"
        :path="path.animated"
        :options="{strokeColor:'#333333'}"
        :zIndex="2">
      </gmap-polyline>

      <!-- DRIVERS -->
      <gmap-marker v-for="(driver, index) in drivers.sector" v-if="driver.location !== null"
        :key="'DS_' + driver.driverId"
        :icon="driver.location.pin"
        :position="{ lat: driver.location.lat, lng: driver.location.lng }"
        :zIndex="4"
        @click="toggleDriverInfo(index)">
      </gmap-marker>

      <gmap-info-window v-for="(driver, index) in drivers.sector" v-if="driver.location !== null && driver.location.showInfo"
        :key="'DI_' + driver.driverId"
        :position="{ lat: driver.location.lat, lng: driver.location.lng }"
        :zIndex="5"
        @closeclick="closeDriverInfo(index)">
        <div class="driver-info">
          <div class="header">
            <div class="photo" v-bind:style="{ 'background-image': 'url(\'' + driver.photo + '\')'}"></div>
            <div class="name"><a :href="'/#/provider/' + driver.driverId" target="_blank">{{ driver.name }}</a></div>
            <div class="email"><a :href="'mailto:' + driver.email">{{ driver.email }}</a></div>
            <div class="email"><a href="javscript:;" @click="callDriver(driver)">{{ driver.phone }}</a></div>
          </div>

          <div class="vehicle">
            <div class="photo" v-bind:style="{ 'background-image': 'url(\'' + driver.vehicle.photoUrl + '\')'}"></div>
            <div class="name">{{ driver.vehicle.manufacturer }} {{ driver.vehicle.model }}</div>
            <div class="plate-number">{{ driver.vehicle.licensePlate }}</div>
          </div>

          <div class="shift-info" v-if="driver.shiftStart !== null && driver.shiftEnd !== null">
            <i class="fa fa-check-circle"></i>
            <span>Available from</span>
            <span class="shift-value">{{ driver.shiftStart.format('hh:mmA') }}</span>
            <span>to</span>
            <span class="shift-value">{{ driver.shiftEnd.format('hh:mmA') }}</span>
          </div>

          <div class="rsvp-info" v-if="driver.rsvpId !== null">
            <i class="fa fa-car"></i>
            <span>Reservation</span>
            <a :href="'/#/reservation/' + driver.rsvpId" >{{ driver.rsvpId }}</a>
            <span>set to clear at</span><br/>
            <span class="highlight">{{ driver.dropoffTime.format('MM/DD hh:mmA')}}.</span>
          </div>

          <div class="row controls" v-if="rsvp !== null">
            <div class="small-6 columns padding-R">
              <a class="button cta xsmall block" href="javascript:;" @click="sendToDriver(driver)">SEND TO {{ driver.firstName }}</a>
            </div>
            <div class="small-6 columns padding-L">
              <a class="button cta xsmall block warning" href="javascript:;" @click="assignToDriver(driver)">ASSIGN TO {{ driver.firstName }}</a>
            </div>
          </div>
          <div class="row controls" v-if="rsvp === null">
            <div class="small-12 columns">
              <a class="button cta xsmall block" href="javascript:;" @click="createShift(driver)">CREATE A SHIFT FOR {{ driver.firstName }}</a>
            </div>
          </div>
          <i class="fa fa-home is-home" v-show="driver.isHomeBase"></i>
        </div>
      </gmap-info-window>
    </gmap-map>

    <div v-bind:class="{ 'untracked-drivers': true, show: hasAvailableDrivers }">
      <div class="title">DRIVERS WITH AVAILABILITY</div>
      <div class="driver" v-for="(driver, index) in drivers.sector" v-if="driver.shiftStart !== null && driver.shiftEnd !== null">
        <div class="header">
          <div class="photo" v-bind:style="{ 'background-image': 'url(\'' + driver.photo + '\')'}"></div>
          <div class="name"><a :href="'/#/provider/' + driver.driverId" target="_blank">{{ driver.name }}</a></div>
          <div class="email"><a :href="'mailto:' + driver.email">{{ driver.email }}</a></div>
          <div class="email"><a href="javscript:;" @click="callDriver(driver)">{{ driver.phone }}</a></div>
        </div>

        <div class="vehicle">
          <div class="photo" v-bind:style="{ 'background-image': 'url(\'' + driver.vehicle.photoUrl + '\')'}"></div>
          <div class="name">{{ driver.vehicle.manufacturer }} {{ driver.vehicle.model }}</div>
          <div class="plate-number">{{ driver.vehicle.licensePlate }}</div>
        </div>

        <div class="shift-info" v-if="driver.shiftStart !== null && driver.shiftEnd !== null">
          <i class="fa fa-check-circle"></i>
          <span>Available from</span>
          <span class="shift-value">{{ driver.shiftStart.format('hh:mmA') }}</span>
          <span>to</span>
          <span class="shift-value">{{ driver.shiftEnd.format('hh:mmA') }}</span>
        </div>

        <div class="rsvp-info" v-if="driver.rsvpId !== null">
          <i class="fa fa-car"></i>
          <span>Reservation</span>
          <a :href="'/#/reservation/' + driver.rsvpId" >{{ driver.rsvpId }}</a>
          <span>set to clear at</span><br/>
          <span class="highlight">{{ driver.dropoffTime.format('MM/DD hh:mmA')}}.</span>
        </div>

        <div class="row controls" v-if="rsvp !== null">
          <div class="small-6 columns padding-R">
            <a class="button cta xsmall block" href="javascript:;" @click="sendToDriver(driver)">SEND TO {{ driver.firstName }}</a>
          </div>
          <div class="small-6 columns padding-L">
            <a class="button cta xsmall block warning" href="javascript:;" @click="assignToDriver(driver)">ASSIGN TO {{ driver.firstName }}</a>
          </div>
        </div>
        <div class="row controls" v-if="rsvp === null">
          <div class="small-12 columns">
            <a class="button cta xsmall block" href="javascript:;" @click="createShift(driver)">CREATE A SHIFT FOR {{ driver.firstName }}</a>
          </div>
        </div>
        <i class="fa fa-home is-home" v-show="driver.isHomeBase"></i>
      </div>
    </div>

    <div v-bind:class="{ 'no-drivers': true, show: drivers.sector.length <= 0 && noDrivers }">
      <i class="far fa-frown fa-2x"></i>
      <div class="message">
        <span>Sorry but we can't find any drivers</span>
        <span v-if="rsvp !== null">
          <span>to cover Reservation</span>
          <a :href="'/#/reservation/' + rsvp.ID" target="_blank">{{ rsvp.ID }}</a>
        </span>
        <span v-if="coverageGap !== null">
          <span>to cover the</span>
          <span class="highlight">{{ coverageGap.start.format('MMM DD hh:mmA') }}</span>
          <span>to</span>
          <span class="highlight">{{ coverageGap.end.format('MMM DD hh:mmA') }}</span>
          <span>shift.</span>
        </span>
      </div>
    </div>

    <div v-bind:class="{ 'submission-modal': true, show: modal.show }">
      <div class="modal-container">
        <div class="modal-content">
          <div class="loader" v-show="modal.loading">
            <i class="fa fa-spinner fa-spin fa-lg"></i>
            <div class="message">{{ modal.loadingMessage }}</div>
          </div>
          <div class="loader" v-show="!modal.loading">
            <i class="fa fa-check-circle fa-lg success-icon" v-show="modal.success"></i>
            <i class="fas fa-exclamation-triangle fa-lg error-icon" v-show="!modal.success"></i>
            <div class="message">{{ modal.doneMessage }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import mapSettings from '@/values/map-settings'
export default {
  name: 'coverage-map',
  data () {
    return {
      mapOptions: mapSettings,
      center: { lat: 33.942791, lng: -118.410042 }, // LAX
      map: {
        labelBackground: {
          url: '',
          anchor: {
            x: 135,
            y: 20
          },
          scaledSize: {
            width: 270,
            height: 40
          }
        },
        poiPin: {
          url: 'https://s3-us-west-2.amazonaws.com/mobile-consumer-app/maps/pins/poi.png',
          anchor: {
            x: 15,
            y: 15
          },
          scaledSize: {
            width: 30,
            height: 30
          }
        }
      },
      path: {
        base: [],
        animated: [],
        animationID: null
      },
      drivers: {
        sector: [],
        nearby: []
      },
      noDrivers: false,
      infoWindows: {
        pickup: false,
        dropoff: false
      },
      rsvpInfo: {
        pickupTime: null,
        dropoffTime: null,
        distanceInMiles: null
      },
      stopsInfo: [],
      modal: {
        show: false,
        loading: false,
        loadingMessage: '',
        success: false,
        doneMessage: ''
      }
    }
  },
  computed: {
    rsvp () { return this.$store.state.coverage.map.rsvp },
    showPolygons () { return this.$store.state.coverage.map.showPolygons },
    sectorItems () {
      let result = []
      let sectors = this.$store.state.coverage.sectors.items
      for (let i = 0; i < sectors.length; i++) {
        result.push(sectors[i
          ])
      }
      return result
    },
    hasUntrackedDrivers () {
      let self = this
      for (let i = 0; i < self.drivers.sector.length; i++) {
        let driver = self.drivers.sector[i]
        if (driver.location === null) {
          return true
        }
      }

      return false;
    },
    hasAvailableDrivers () {
      let self = this
      for (let i = 0; i < self.drivers.sector.length; i++) {
        let driver = self.drivers.sector[i]
        if (driver.shiftStart !== null && driver.shiftEnd !== null) {
          return true
        }
      }

      return false;
    },
    coverageGap () { return this.$store.state.coverage.map.gap }
  },
  methods: {
    createShift (driver) {
      this.$store.commit('coverage/setCoverageShiftFormDriver', driver)
      this.$store.commit('coverage/setCoverageShiftFormGap', this.coverageGap)
      this.$store.commit('coverage/setCoverageShiftFormSector', this.$store.state.coverage.map.sector)
      this.$store.commit('coverage/setCoverageShiftFormShow', true)
    },
    sendToDriver (driver) {
      let self = this
      if (!self.rsvp.isPackage) {
        self.modal = {
          show: true,
          loading: true,
          loadingMessage: 'Sending Reservation ' + self.rsvp.ID + ' to ' + driver.firstName + '...',
          success: false,
          doneMessage: ''
        }
      } else {
        self.modal = {
          show: true,
          loading: true,
          loadingMessage: 'Sending Package ' + self.rsvp.package.id + ' to ' + driver.firstName + '...',
          success: false,
          doneMessage: ''
        }

      }

      let url = ''
      if (!self.rsvp.isPackage) {
        url = '/admin/rsvp/' + self.rsvp.ID + '/send'
      } else {
        url = '/admin/package/' + self.rsvp.package.id + '/send'
      }

      self.$sdk.post(url, {
        token: self.$store.state.session.sessionToken,
        provId: driver.driverId
      }, r => {
        if (r.error) {
          self.modal.loading = false
          self.modal.success = false
          self.modal.doneMessage = 'Oops! Something went wrong. Please try again later.'
        } else {
          self.modal.loading = false
          self.modal.success = true
          let message = ''
          if (!self.rsvp.isPackage) {
            message = 'Sent Reservation ' + self.rsvp.ID + ' to ' + driver.firstName + '.'
          } else {
            message = 'Sent Package ' + self.rsvp.package.id + ' to ' + driver.firstName + '.'
          }
          self.modal.doneMessage = message
        }
        setTimeout(() => {
          self.modal.show = false
        }, 1000)
      })
    },
    assignToDriver (driver) {
      let self = this
      if (!self.rsvp.isPackage) {
        self.modal = {
          show: true,
          loading: true,
          loadingMessage: 'Assigning Reservation ' + self.rsvp.ID + ' to ' + driver.firstName + '...',
          success: false,
          doneMessage: ''
        }
      } else {
        self.modal = {
          show: true,
          loading: true,
          loadingMessage: 'Assigning Package ' + self.rsvp.package.id + ' to ' + driver.firstName + '...',
          success: false,
          doneMessage: ''
        }
      }

      let request = null
      if (!self.rsvp.isPackage) {
        request = {
          token: self.$store.state.session.sessionToken,
          rsvpId: self.rsvp.ID
        }
      } else {
        request = {
          token: self.$store.state.session.sessionToken,
          packageId: self.rsvp.package.id
        }
      }

      self.$sdk.post('/provider/' + driver.driverId + '/assign', request, r => {
        if (r.error) {
          self.modal.loading = false
          self.modal.success = false
          self.modal.doneMessage = 'Oops! Something went wrong. Please try again later.'
        } else {
          self.modal.loading = false
          self.modal.success = true
          let message = ''
          if (!self.rsvp.isPackage) {
            message = 'Assigned Reservation ' + self.rsvp.ID + ' to ' + driver.firstName + '.'
          } else {
            message = 'Assigned Package ' + self.rsvp.package.id + ' to ' + driver.firstName + '.'
          }
          self.modal.doneMessage = message
          self.$store.commit('coverage/setCoverageSectorsRefresh', true)

          self.$store.commit('coverage/setCoverageMapShowPolygons', true)
          self.$store.commit('coverage/setCoverageMapRsvp', null)
        }
        setTimeout(() => {
          self.modal.show = false
        }, 1000)
      })
    },
    getCenter (sector) {
      if (sector.centerPointLat !== null && sector.centerPointLng !== null) {
        return {
          lat: sector.centerPointLat,
          lng: sector.centerPointLng
        }
      }
      let points = sector.polygon
      let bounds = new google.maps.LatLngBounds() // eslint-disable-line no-undef

      for (let i = 0; i < points.length; i++) {
        let point = points[i]
        bounds.extend(
          new google.maps.LatLng( // eslint-disable-line no-undef
            point.lat,
            point.lng
          )
        )
      }

      let result = bounds.getCenter()
      return result
    },
    centerSector (sector, index) {
      let self = this
      self.$store.commit('coverage/resetCoverageSectorPolygonOption')
      self.$store.commit('coverage/setCoverageSectorPolygonOption', {
        id: sector.ID,
        polygonOptions: {
          zIndex: 2,
          strokeWeight: 5,
          fillOpacity: 0.75
        }
      })
      self.setBounds(sector.polygon)

      if (!self.sectorItems[index].showPanel) {
        self.$store.commit('coverage/setCoverageSectorsPanelShow', index)
        self.$store.commit('coverage/setCoverageSectorScrollElement', sector.identifier)
      }
    },
    setBounds (points) {
      let self = this

      let bounds = new google.maps.LatLngBounds() // eslint-disable-line no-undef

      for (let i = 0; i < points.length; i++) {
        let point = points[i]
        bounds.extend(
          new google.maps.LatLng( // eslint-disable-line no-undef
            point.lat,
            point.lng
          )
        )
      }
      self.$refs.CoverageMap.$mapObject.fitBounds(bounds)
    },
    getRoute (request, callback) {
      let direction = new google.maps.DirectionsService() // eslint-disable-line no-undef
      direction.route(request, (response, status) => {
        if (status !== google.maps.DirectionsStatus.OK) { // eslint-disable-line no-undef
          return
        }
        callback(response)
      })
    },
    resetCenter () {
      let self = this

      let points = []
      for (let i = 0; i < self.sectorItems.length; i++) {
        if (self.sectorItems[i].polygon === null) {
          continue
        }
        points = points.concat(self.sectorItems[i].polygon)
      }

      if (points.length === 0) {
        self.$refs.CoverageMap.$mapObject.panTo({ lat: 33.942791, lng: -118.410042 })
        return
      }

      self.setBounds(points)
    },
    findDriverIndexInList (id, list) {
      let result = null
      for (let i = 0; i < list.length; i++) {
        if (id === list[i].driverId) {
          return i
        }
      }
      return result
    },
    findDrivers (forShift) {
      let self = this

      let pickupTime = null 
      if (self.rsvp !== null) {
        if (!self.rsvp.isPackage) {
          pickupTime = moment(self.rsvp.pickupDate, 'YYYY-MM-DD HH:mm:ss Z')
        } else {
          pickupTime = moment(self.rsvp.package.localPickupTime, 'YYYY-MM-DD HH:mm:ss Z')
        }
      }
      let sector = self.$store.state.coverage.map.sector
      let gap = self.$store.state.coverage.map.gap

      let drivers = []
      self.drivers.sector = []
      self.noDrivers = false
      // home base drivers
      for (let i = 0; i < sector.homebaseDrivers.length; i++) {
        let driver = sector.homebaseDrivers[i]
        if (driver.services.indexOf(self.rsvp.serviceTypeId) < 0) {
          continue
        }
        drivers.push({
          driverId: driver.driverId,
          name: driver.firstName + ' ' + driver.lastName,
          phone: driver.phoneNumber,
          email: driver.email,
          isHomeBase: true,
          packageId: null,
          rsvpId: null,
          pickupTime: null,
          shiftStart: null,
          shiftEnd: null,
          location: null,
          photo: null,
          services: driver.services
        })
      }
      // working drivers
      for (let i = 0; i < sector.workDrivers.length; i++) {
        let driver = sector.workDrivers[i]
        if (driver.services.indexOf(self.rsvp.serviceTypeId) < 0) {
          continue
        }
        let index = self.findDriverIndexInList(driver.driverId, drivers)
        if (index === null) {
          drivers.push({
            driverId: driver.driverId,
            name: driver.firstName + ' ' + driver.lastName,
            phone: null,
            email: null,
            isHomeBase: false,
            packageId: null,
            rsvpId: null,
            pickupTime: null,
            shiftStart: driver.shiftStart,
            shiftEnd: driver.shiftEnd,
            location: null,
            photo: null,
            services: driver.services
          })
        } else {
          let start = driver.shiftStart.unix()
          let end = driver.shiftEnd.unix()
          if (self.rsvp !== null) {
            if (drivers[index].shiftStart === null &&
                drivers[index].shiftEnd === null) {
              let puTime = pickupTime.unix()
              if (puTime >= start && puTime <= end) {
                drivers[index].shiftStart = driver.shiftStart
                drivers[index].shiftEnd = driver.shiftEnd
              }
            }
          }

          if (gap !== null) {
            let gapStart = gap.start.unix()

            if (gapStart >= start && gapStart <= end) {
              drivers[index].shiftStart = driver.shiftStart
              drivers[index].shiftEnd = driver.shiftEnd
            }
          }
        }
      }
      
      // dropoff drivers
      for (let i = 0; i < sector.driverDropoff.length; i++) {
        let driver = sector.driverDropoff[i]
        if (driver.services.indexOf(self.rsvp.serviceTypeId) < 0) {
          continue
        }
        let index = self.findDriverIndexInList(driver.driverId, drivers)

        let dropoffTime = driver.dropoffTime
        if (index === null) {
          let insertDriver = false
          if (self.rsvp !== null) {
            let puTime = pickupTime.unix()
            if (Math.abs(puTime - dropoffTime) <= 3600) { // clearing time within the hour
              insertDriver = true
            }
          }

          if (gap !== null) {
            let gapStart = gap.start.unix()
            let gapEnd = gap.end.unix()

            if (dropoffTime >= gapStart && dropoffTime <= gapEnd) {
              insertDriver = true
            }
          }

          if (insertDriver) {
            drivers.push({
              driverId: driver.driverId,
              name: driver.firstName + ' ' + driver.lastName,
              phone: null,
              email: null,
              isHomeBase: false,
              packageId: driver.packageID,
              rsvpId: driver.rsvpId,
              pickupTime: moment.unix(driver.pickupTime),
              dropoffTime: moment.unix(driver.dropoffTime),
              shiftStart: null,
              shiftEnd: null,
              location: null,
              photo: null,
              services: driver.services
            })
          }
        } else {
          if (self.rsvp !== null) {
            let puTime = pickupTime.unix()
            if (Math.abs(puTime - dropoffTime) <= 3600) { // clearing time within the hour
              drivers[index].packageId = driver.packageID
              drivers[index].rsvpId = driver.rsvpId
              drivers[index].pickupTime = moment.unix(driver.pickupTime)
              drivers[index]['dropoffTime'] = moment.unix(driver.dropoffTime)
            }
          }

          if (gap !== null) {
            let gapStart = gap.start.unix()
            let gapEnd = gap.end.unix()

            if (dropoffTime >= gapStart && dropoffTime <= gapEnd) {
              drivers[index].packageId = driver.packageID
              drivers[index].rsvpId = driver.rsvpId
              drivers[index].pickupTime = moment.unix(driver.pickupTime)
              drivers[index]['dropoffTime'] = moment.unix(driver.dropoffTime)
            }
          }
        }
      }

      let ids = []
      for (let i = 0; i < drivers.length; i++) {
        ids.push(drivers[i].driverId)
      }
      self.$sdk.get('/provider', {
        token: self.$store.state.session.sessionToken,
        ids: ids
      }, r => {
        if (r.error) {
          // show error
        } else {
          for (let i = 0; i < r.list.length; i++) {
            let driver = r.list[i]
            let index = self.findDriverIndexInList(driver.ID, drivers)
            if (index === null) {
              continue
            }

            drivers[index].phone = '' + driver.phone
            drivers[index].email = driver.email
            drivers[index].photo = driver.avatarUrl
            drivers[index]['vehicle'] = driver.vehicle
            drivers[index]['firstName'] = driver.firstName
            drivers[index]['lastName'] = driver.lastName
            if (driver.tracks !== null) {
              let heading = parseInt(driver.tracks.heading)
              
              if (isNaN(heading) || heading < 0 || heading > 359) {
                if (self.rsvp !== null) {
                  let location = null
                  if (!self.rsvp.isPackage) {
                    location = self.rsvp.pickupLocation
                  } else {
                    location = self.rsvp.stops[0]
                  }
                  heading = self.computeHeading(driver.tracks, location)
                } else {
                  heading = Math.floor(Math.random() * Math.floor(360))
                }
              }
              drivers[index].location = {
                lat: driver.tracks.lat,
                lng: driver.tracks.lng,
                heading: heading,
                pin: self.renderDriverPin(heading),
                showInfo: false
              }
            }
          }
        }

        self.drivers.sector = drivers
        self.noDrivers = false

        if (drivers.length <= 0) {
          self.noDrivers = true
        }
        self.centerDrivers()

        if (forShift) {
        }
      })
    },
    centerDrivers () {
      let self = this
      let points = []

      for (let i = 0; i < self.drivers.sector.length; i++) {
        let driver = self.drivers.sector[i]
        if (driver.location === null) {
          continue
        }
        points.push({
          lat: driver.location.lat,
          lng: driver.location.lng
        })
      }

      if (points.length <= 0) {
        return
      }
      self.setBounds(points)
    },
    renderDriverPin (heading) {
      return {
        url: 'https://s3-us-west-2.amazonaws.com/mobile-consumer-app/maps/car-sprite.png',
        origin: new google.maps.Point(heading * 60, 0), // eslint-disable-line no-undef
        anchor: {
          x: 30,
          y: 30
        },
        size: {
          width: 60,
          height: 60
        }
      }
    },
    computeHeading (orig, dest) {
      let y = Math.sin(dest.lng - orig.lng) * Math.cos(dest.lat)
      let x = Math.cos(orig.lat) * Math.sin(dest.lat) -
              Math.sin(orig.lat) * Math.cos(dest.lat) * Math.cos(dest.lng - orig.lng)

      let brng = Math.atan2(y, x) * 180 / Math.PI
      return Math.round(360 - ((brng + 360) % 360))
    },
    toggleDriverInfo (index) { this.drivers.sector[index].location.showInfo = !this.drivers.sector[index].location.showInfo },
    closeDriverInfo (index) { this.drivers.sector[index].location.showInfo = false },
    callDriver (driver) {
      let self = this
      self.$plugins.phone.startCall(self, {
        phone: driver.phone,
        name: driver.firstName + ' ' + driver.lastName
      })
    },
    toggleStopsInfo(index) {
      this.stopsInfo[index] = !this.stopsInfo[index]
    }
  },
  watch: {
    sectorItems (value) { this.resetCenter() },
    coverageGap (value) {
      let self = this
      if (value === null) {
        self.infoWindows.pickup = false
        self.infoWindows.dropoff = false
        self.drivers.sector = []
        self.noDrivers = false
        self.drivers.nearby = []
        self.resetCenter()
        return
      }
      self.findDrivers(true)
    },
    rsvp (value) {
      let self = this
      self.path.base = []
      self.path.animated = []
      if (self.path.animationID !== null) {
        clearInterval(self.path.animationID)
        self.path.animationID = null
      }
      if (value === null) {
        self.stopsInfo = []
        self.infoWindows.pickup = false
        self.infoWindows.dropoff = false
        self.drivers.sector = []
        self.noDrivers = false
        self.drivers.nearby = []
        self.resetCenter()
        return
      }

      self.findDrivers(false)
      self.infoWindows.pickup = true
      self.infoWindows.dropoff = true

      let request = null
      if (!value.isPackage) {
        request = {
          origin: {
            lat: value.pickupLocation.lat,
            lng: value.pickupLocation.lng
          },
          destination: {
            lat: value.dropoffLocation.lat,
            lng: value.dropoffLocation.lng
          },
          travelMode: google.maps.TravelMode.DRIVING // eslint-disable-line no-undef
        }
      } else {
        let waypoints = value.stops.slice(0, value.stops.length)

        request = {
          origin: waypoints.shift(),
          destination: waypoints.pop(),
          travelMode: google.maps.TravelMode.DRIVING // eslint-disable-line no-undef
        }

        let stopovers = []
        for (let i = 0; i < waypoints.length; i++) {
          stopovers.push({
            location: { lat: waypoints[i].lat, lng: waypoints[i].lng },
            stopover: false
          })
        }

        if (stopovers.length > 0) {
          request['waypoints'] = stopovers
        }

        self.stopsInfo = []
        for (let i = 0; i < value.stops.length; i++) {
          self.stopsInfo.push(true)
        }
      }

      self.rsvpInfo.pickupTime = moment(value.pickupDate, 'YYYY-MM-DD HH:mm:ss Z')

      self.getRoute(request, response => {
        let legs = response.routes[0].legs
        let polylinePath = []
        let bounds = []
        let meters = 0
        let seconds = 0
        for (let j = 0; j < legs.length; j++) {
          let steps = legs[j].steps
          meters = meters + legs[j].distance.value
          seconds = seconds + legs[j].duration.value

          for (let k = 0; k < steps.length; k++) {
            let paths = steps[k].path
            for (let l = 0; l < paths.length; l++) {
              bounds.push({
                lat: paths[l].lat(),
                lng: paths[l].lng()
              })
              polylinePath.push(paths[l])
            }
          }
        }
        self.rsvpInfo.dropoffTime = moment(value.pickupDate, 'YYYY-MM-DD HH:mm:ss Z').add(seconds, 'seconds')
        self.rsvpInfo.distanceInMiles = meters * 0.000621371
        self.path.base = polylinePath
        self.setBounds(bounds)

        let size = polylinePath.length
        let itemsPerFrame = Math.floor(size / 30)
        let restartAt = itemsPerFrame * 30
        let start = 0
        let end = itemsPerFrame - 1
        let startSplice = false
        self.path.animationID = setInterval(() => {
          if (end >= restartAt) {
            if (!startSplice && self.path.animated.length < size) { // add remaining
              self.path.animated = polylinePath.slice(0)
              startSplice = true
              return
            }

            if (self.path.animated.length > 0) {
              self.path.animated.splice(0, itemsPerFrame)
              return
            }

            start = 0
            end = itemsPerFrame - 1
            startSplice = false
            self.path.animated = []
            return
          }
          let itemsToAdd = polylinePath.slice(start, end)
          for (let i = 0; i < itemsToAdd.length; i++) {
            self.path.animated.push(itemsToAdd[i])
          }
          start = end + 1
          end = start + itemsPerFrame
        }, 33)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.coverage-map{
  width: 100%;
  height: 100%;
}

.driver-info {
  font-family: 'Open Sans', sans-serif;
  padding: 5px 0 0 5px;
  & .header {
    position: relative;
    min-height: 50px;
    padding-left: 60px;
    padding-top: 0px;
  }

  & .photo {
    position: absolute;
    top: 0;
    left: 0;
    width: 50px;
    height: 50px;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 50px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    border: 1px solid rgba(0,0,0,0.3);
  }

  & .name {
    font-size: 14px;
    line-height: 14px;
    font-weight: 700;
  }

  & .email {
    font-size: 12px;
    line-height: 12px;
    margin-top: 5px;
  }

  & .vehicle {
    margin-top: 10px;
    position: relative;
    min-height: 50px;
    padding-left: 60px;
    padding-top: 10px;
  }

  & .shift-info {
    font-size: 14px;
    line-height: 14px;
    margin-top: 15px;
    text-align: center;

    & > * {
      display: inline-block;
      vertical-align: middle;
    }

    & i { color: #26D97A; }

    & .shift-value {
      font-weight: 700;
      color: #0D79F2;
    }
  }

  & .rsvp-info {
    font-size: 14px;
    line-height: 16px;
    margin-top: 15px;
    text-align: center;

    & .highlight, a {
      font-weight: 700;
      color: #0D79F2;
    }
  }

  & .controls {
    margin-top: 15px;

    & .padding-R { padding-right: 3px; }
    & .padding-L { padding-left: 3px; }

    & .button {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  & .is-home {
    position: absolute;
    top: 15px;
    right: 15px;
    width: 30px;
    height: 30px;
    border-radius: 30px;
    background-color: #26D97A;
    text-align: center;
    line-height: 30px;
    color: #fff;
  }
}

.no-drivers {
  position: absolute;
  top: 12px;
  right: -400px;
  background-color: #fff;
  text-align: center;
  width: 300px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.35);
  border-radius: 3px;
  transition: all 250ms;
  font-size: 14px;
  padding: 15px;

  &.show { right: 15px; }

  & .message {
    margin-top: 10px;
    & > * {
      display: inline-block;
      vertical-align: middle;
    }

    & .highlight {
      font-weight: 700;
    }
  }
}
.untracked-drivers {
  position: absolute;
  top: 12px;
  right: -400px;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0,0,0,0.35);
  border-radius: 3px;
  width: 350px;
  transition: all 250ms;
  max-height: 90%;
  overflow-y: auto;
  overflow-x: hidden;

  &.show { right: 15px; }

  & .title {
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;
    padding: 10px 0 7px;
    color: #555; // #0D79F2;
  }

  & .driver {
    padding: 10px 10px 5px;
    border-top: 1px solid rgba(0,0,0,0.2);
    position: relative;
  }

  & .is-home {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border-radius: 30px;
    background-color: #26D97A;
    text-align: center;
    line-height: 30px;
    color: #fff;
  }

  & .header {
    position: relative;
    min-height: 50px;
    padding-left: 60px;

    & .photo {
      position: absolute;
      top: 0;
      left: 0;
      width: 50px;
      height: 50px;
      background-position: 50% 50%;
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: 50px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      border: 1px solid rgba(0,0,0,0.3);
    }

    & .name {
      font-size: 14px;
      line-height: 14px;
      font-weight: 700;
    }

    & .email {
      font-size: 12px;
      line-height: 12px;
      margin-top: 5px;
    }
  }

  & .vehicle {
    position: relative;
    padding-left: 60px;
    padding-top: 8px;
    margin-top: 5px;
    height: 50px;

    & .photo {
      position: absolute;
      top: 0;
      left: 0;
      width: 50px;
      height: 50px;
      background-position: 50% 50%;
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: 50px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      border: 1px solid rgba(0,0,0,0.3);
    }

    & .name {
      font-size: 14px;
      font-weight: 700;
      line-height: 14px;
    }

    & .plate-number {
      font-size: 14px;
      line-height: 14px;
      margin-top: 5px;
    }
  }

  & .shift-info {
    font-size: 14px;
    line-height: 14px;
    margin-top: 15px;
    text-align: center;

    & > * {
      display: inline-block;
      vertical-align: middle;
    }

    & i { color: #26D97A; }

    & .shift-value {
      font-weight: 700;
      color: #0D79F2;
    }
  }

  & .rsvp-info {
    font-size: 14px;
    line-height: 16px;
    margin-top: 15px;
    text-align: center;

    & .highlight, a {
      font-weight: 700;
      color: #0D79F2;
    }
  }

  & .controls {
    margin-top: 15px;

    & .padding-R { padding-right: 3px; }
    & .padding-L { padding-left: 3px; }

    & .button {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.point-info {
  padding: 0 0 5px 5px;
  background-color: #fff;
  font-family: 'Open Sans', sans-serif;

  & label {
    font-weight: 700;
    font-size: 10px;
    line-height: 10px;
    color: #0D79F2;
  }

  & .value {
    font-size: 14px;
    font-weight: 700;
  }

  & .label-value:not(:first-child) { margin-top: 10px; }
}

.submission-modal {
  display: table;
  position: fixed;
  top: -100%;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  background-color: rgba(0,0,0,0.5);
  z-index: 12;
  transition: all 250ms;

  &.show { top: 0; opacity: 1; }

  & .modal-container {
    display: table-cell;
    vertical-align: middle;
    text-align: center;
  }

  & .modal-content {
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    width: 350px;
    margin: 0 auto;
    padding: 20px 15px;
    border-radius: 5px;
  }

  & .loader .message {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    margin-top: 10px;
  }

  & .success-icon { color: #26D97A; }
  & .error-icon { color: #F25E0D; }
}
</style>



// WEBPACK FOOTER //
// src/components/Coverage/Map.vue